import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { QRCodeCanvas } from "qrcode.react";
import ContainerLayout from "../common/containerLayout";
import mowi from "../generateQR/mowi.png";
import { ChangeEvent, useEffect, useState } from "react";
import { selectCurrentAdminId } from "../../utils/helpers/selectorHelper";
import {
  createAdminReferralQr,
  getAdminReferralQrByAdminId,
} from "../../services/adminReferralQrApi";
import { AdminReferralQrDTO } from "../../shared/dtos/adminReferralQrDTO";
import { LoadingOutlined } from "@ant-design/icons";

const { Text } = Typography;

const qrUrlValue = process.env.REACT_APP_REFERRAL_QR_CODE_URL;

const initialFormValues: AdminReferralQrDTO = {
  id: undefined,
  code: "",
  qrSize: 256,
  imgQrSize: 32,
  bgColor: "#FFFFFF",
  fgColor: "#000000",
  errorLevel: "M",
};

const GenerateReferralQR = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<AdminReferralQrDTO>();

  Form.useWatch("code", form);
  Form.useWatch("qrSize", form);
  Form.useWatch("bgColor", form);
  Form.useWatch("imgQrSize", form);
  Form.useWatch("fgColor", form);
  Form.useWatch("errorLevel", form);

  const downloadQRCode = () => {
    const canvas = document.querySelector("#qr-code") as HTMLCanvasElement;
    if (!canvas) throw new Error("<canvas> not found in the DOM");

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "QR code.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  useEffect(() => {
    const getQr = async () => {
      setLoading(true);
      const response: AdminReferralQrDTO = await getAdminReferralQrByAdminId();

      if (response?.id) form.setFieldsValue(response);
      else form.setFieldsValue(initialFormValues);

      setLoading(false);
    };

    getQr();
  }, [form]);

  const onChange = (value: any, name: string) => {
    switch (name) {
      case "qrSize":
        const imgQrSize = (15 * +value) / 100;
        form.setFieldValue("imgQrSize", imgQrSize);
        break;
      default:
        break;
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      await createAdminReferralQr({
        ...form.getFieldsValue(true),
        adminId: selectCurrentAdminId(),
      });

      message.success("QR generado satisfactoriamente.");
    } catch (error: any) {
      if (error.response.status === 409)
        message.error("Ya existe un QR para este cliente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerLayout
      title="QR de referidos"
      showNotification={false}
      justify={"center"}
    >
      {loading && (
        <Col span={24} className="loading-container error-container">
          <LoadingOutlined />
        </Col>
      )}
      {!loading && (
        <Col span={24}>
          <Row gutter={[32, 16]} justify="center">
            {!form.getFieldValue("id") && (
              <Col span={6}>
                <Form
                  name="referredQr"
                  form={form}
                  onFinish={save}
                  layout="vertical"
                >
                  <Form.Item
                    name="qrSize"
                    label="Tamaño"
                    rules={[{ required: true, message: "Ingrese un tamaño" }]}
                  >
                    <Input
                      type="number"
                      placeholder="356"
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onChange(event.target.value, "qrSize")
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="errorLevel"
                    label="Nivel de corrección"
                    rules={[
                      {
                        required: true,
                        message: "Ingrese un nivel de corrección",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Seleccine el nivel de corrección"
                      options={[
                        {
                          value: "L",
                          label: "L",
                        },
                        {
                          value: "M",
                          label: "M",
                        },
                        {
                          value: "Q",
                          label: "Q",
                        },
                        {
                          value: "H",
                          label: "H",
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="fgColor"
                    label="Color"
                    rules={[{ required: true, message: "Ingrese un color" }]}
                  >
                    <Input type="color" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Generar QR
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            )}
            <Col span={6}>
              <Space direction="vertical">
                <QRCodeCanvas
                  id="qr-code"
                  value={`${qrUrlValue}${form.getFieldValue("code")}`}
                  size={form.getFieldValue("qrSize")}
                  bgColor={form.getFieldValue("bgColor")}
                  fgColor={form.getFieldValue("fgColor")}
                  level={form.getFieldValue("errorLevel")}
                  includeMargin={false}
                  imageSettings={{
                    src: mowi,
                    x: undefined,
                    y: undefined,
                    height: form.getFieldValue("imgQrSize"),
                    width: form.getFieldValue("imgQrSize"),
                    excavate: true,
                  }}
                />
                {!form.getFieldValue("id") && (
                  <Col span={24}>
                    <Alert
                      message={
                        <>
                          <Col span={24} style={{ textAlign: "center" }}>
                            <Text>Vista previa del QR</Text>
                          </Col>
                          <Col span={24} style={{ textAlign: "center" }}>
                            <Text strong>No válido para ser usado!</Text>
                          </Col>
                        </>
                      }
                      type="warning"
                    />
                  </Col>
                )}
                {form.getFieldValue("id") && (
                  <Col span={24}>
                    <Button type="primary" onClick={downloadQRCode}>
                      Descargar
                    </Button>
                  </Col>
                )}
              </Space>
            </Col>
          </Row>
        </Col>
      )}
    </ContainerLayout>
  );
};

export default GenerateReferralQR;
